import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  UserService,
  UserServiceState,
} from '../../core/services/user.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DateFormatter } from '../../utils/DateFormatter';
import { Router, RouterLink } from '@angular/router';
import {
  AttempsUserResponse,
  DocumentType,
  documentTypeList,
} from '../../core/models/user.model';
import { Portal } from '../../shared/layout/portal/portal';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { SimService } from '../../core/services/sim.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-activation-form',
  imports: [
    ReactiveFormsModule,
    Portal,
    CommonModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatIconModule,
    RouterLink,
  ],
  templateUrl: './activation-form.html',
  styleUrl: './activation-form.scss',
})
export class ActivationForm implements OnInit, OnDestroy {
  dateFormatter = new DateFormatter();
  MAX_RETRY =3;
  userAgreement: boolean = false;

  activationForm!: FormGroup;
  maxDate: Date = new Date();
  isLoading: boolean = false;
  isValidUser: boolean = false;
  errorValidating: boolean = false;
  retries: number = 0;
  userCancel: boolean = false;

  documentType = documentTypeList;
  authToken: string = '';

  currentUser: UserServiceState | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private simService: SimService
  ) {
    this.userService.currentUser$.subscribe((data) => {
      this.currentUser = data;
    });
  }

  ngOnInit(): void {
    this.activationForm = this.fb.group({
      documentType: ['', Validators.required],
      id: [null, [Validators.required, Validators.maxLength(10)]],
      expeditionDate: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
    });

    this.activationForm
      .get('documentType')
      ?.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((documentType) => {
        const expeditionControl = this.activationForm.get('expeditionDate');

        if (documentType === 'NIT') {
          expeditionControl?.setValidators(null);
        } else {
          expeditionControl?.setValidators(Validators.required);
        }

        expeditionControl?.updateValueAndValidity();
      });
  }

 onSubmit() {
   this.isLoading =  true;
   if (!this.activationForm.valid) {
     this.activationForm.markAllAsTouched();
     this.isLoading = false;
    return;
   }
     if(this.activationForm.get('documentType')?.value === 'NIT'){
       return this.validateUser(0);
     }else {
       this.userService.canUseService(
              this.activationForm.get('documentType')?.value,
              this.activationForm.get('id')?.value,
              this.dateFormatter.getYYYYMMDD(this.activationForm.get('expeditionDate')?.value)
           )
        .subscribe((data: AttempsUserResponse) => {
          if(data.data.attempts > this.MAX_RETRY){
           return this.router.navigate(['manual-activation'])
          }
          return this.validateUser(data.data.attempts);
        }, err => this.router.navigate(['technical-failure'])) 
     }
  }

  private validateUser(attemps: number) {
    const issueDateState =
      this.activationForm.get('documentType')?.value !== 'NIT'
        ? this.dateFormatter.getYYYYMMDD(
            this.activationForm.get('expeditionDate')?.value,
          )
        : '';

    if (environment.skipUserValidation) {
      this.completeValidationFlow(issueDateState);
      return;
    }

    this.userService
      .validateUserData({
        documentNumber: this.activationForm.get('id')?.value,
        documentType: this.activationForm.get('documentType')?.value,
        expeditionDate: issueDateState,
      })
      .subscribe(
        (userValidationResult) => {
          if (userValidationResult.data.valid) {
            this.completeValidationFlow(issueDateState);
          } else {
            this.router.navigate(['user-retry', attemps]);
          }
        },
        (err) => {
          this.isLoading = false;
          this.router.navigate(['user-retry', attemps]);
        },
      );
  }

  private completeValidationFlow(issueDateState: string) {
    const formattedIssueDate = issueDateState
      ? this.dateFormatter.getDottedDDMMYYYY(
          this.activationForm.get('expeditionDate')?.value,
        )
      : '';
    const updatedUser = {
      ...this.currentUser?.user,
      identificationNumber: this.activationForm.get('id')?.value,
      identificationType: this.activationForm.get('documentType')?.value,
      issueDate: formattedIssueDate,
      email: this.activationForm.get('email')?.value,
      identityVerified: true,
    };

    this.userService.setState({
      user: updatedUser,
      valid: true,
    });

    const activateSimCardData = { ...updatedUser };
    if (activateSimCardData.identificationType === DocumentType.NIT) {
      activateSimCardData.issueDate = null;
    }

    this.simService.activateSimCard(activateSimCardData).subscribe(
      (data) => {
        if (data.success) {
          this.isLoading = false;
          this.router.navigate(['activation-success']);
        }
        this.isLoading = false;
      },
      (err) => this.router.navigate(['technical-failure']),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
